/**
 * <next-link> - Unified web component for navigation and modal actions.
 *
 * Modes:
 *   mode="link"   -> Full page redirect (default)
 *   mode="modal"  -> HTMX-powered modal loading
 *
 * Common attributes:
 *   module     - If "PORTAL", base URL becomes PORTAL/Next.do
 *   data-*     - All data attributes become URL query params
 *
 * Link-only attributes:
 *  endpoint   - Appended as ?endpoint=xxx to Next.do
 *
 * Modal-only attributes:
 *   method           - "get" (default) or "post"
 *   ids-from         - Dot-path to a window array (e.g. "app.selectedIds")
 *   max-ids          - Max allowed IDs before disabling
 *   disable-if-empty - Disable when ids-from resolves to []
 *   url              - HTMX endpoint to load into #next-modal-container
 *
 * Examples:
 *   <next-link endpoint="<%=KibePeriodDateEndpoint.List.getFullName()%>" data-userId="123">Go to page</next-link>
 *
 *   <next-link mode="modal" url="<%=KibePeriodDateEndpoint.List.getUrl(request.getContextPath())%>" method="post"
 *                ids-from="app.selectedIds" disable-if-empty>
 *     Open Modal
 *   </next-link>
 */
class NextLink extends HTMLElement {

    _connected = false;

    static get observedAttributes() {
        return ['ids-from', 'max-ids', 'disable-if-empty'];
    }

    connectedCallback() {
        this.style.cursor = 'pointer';
        this.addEventListener('click', this._onClick.bind(this));

        if (this.mode === 'modal') {
            this._updateDisabledState();
        }

        this._connected = true;
    }

    attributeChangedCallback() {
        if (!this._connected || this.mode !== 'modal') return;
        this._updateDisabledState();
    }

    // -- Properties ----------------------------------------------------

    get mode() {
        return (this.getAttribute('mode') || 'link').toLowerCase();
    }

    // -- Shared --------------------------------------------------------

    _buildUrl() {
        const endpoint = this.getAttribute('endpoint') || '';
        const module = this.getAttribute('module') || '';
        const params = new URLSearchParams(this.dataset);
        params.delete('mode');
        params.append('endpoint', endpoint);

        const baseUrl = module === 'PORTAL' ? 'PORTAL/Next.do' : 'Next.do';
        return `${baseUrl}?${params.toString()}`;
    }

    _onClick(e) {
        e.preventDefault();

        if (this.getAttribute('aria-disabled') === 'true') return;

        if (this.mode === 'modal') {
            this._openModal();
        } else {
            window.location.href = this._buildUrl();
        }
    }

    // -- Shadow DOM helpers --------------------------------------------

    /**
     * Find the modal container, checking shadow DOM first, then light DOM.
     */
    _findModalContainer() {
        // 1. If this next-link is inside a shadow root, look there
        const root = this.getRootNode();
        if (root instanceof ShadowRoot) {
            const container = root.getElementById('next-modal-container');
            if (container) return container;
        }

        // 2. Look inside the <next-htmx> shadow root
        const nextHtmx = document.getElementById('next-htmx');
        if (nextHtmx?.shadowRoot) {
            const container = nextHtmx.shadowRoot.getElementById('next-modal-container');
            if (container) return container;
        }

        // 3. Fallback to light DOM
        return document.querySelector('#next-modal-container');
    }

    // -- Modal ---------------------------------------------------------

    _resolvePath(path) {
        return path.split('.').reduce((obj, key) => obj?.[key], window);
    }

    _getArrayFromAttribute(attrName) {
        const path = this.getAttribute(attrName);
        if (!path) return [];
        return this._resolvePath(path);
    }

    _getIds() {
        return this._getArrayFromAttribute('ids-from');
    }

    _getIdSourceType() {
        return this._getArrayFromAttribute('id-source-type');
    }

    _updateDisabledState() {
        const ids = this._getIds();
        const disableIfEmpty = this.hasAttribute('disable-if-empty');
        const maxIdsAttr = this.getAttribute('max-ids');
        const maxIds = maxIdsAttr ? parseInt(maxIdsAttr, 10) : null;

        const disabled =
            (disableIfEmpty && ids.length === 0) ||
            (maxIds !== null && ids.length > maxIds);

        if (disabled) {
            this.setAttribute('aria-disabled', 'true');
            this.style.pointerEvents = 'none';
            this.style.opacity = '0.5';
        } else {
            this.removeAttribute('aria-disabled');
            this.style.pointerEvents = '';
            this.style.opacity = '';
        }
    }

    _openModal() {
        const url = this.getAttribute('url');
        const method = (this.getAttribute('method') || 'get').toLowerCase();
        const hostEl = this._findModalContainer(); // this finds <next-htmx id="next-modal-container">

        if (!hostEl) {
            console.warn('<next-link> could not find #next-modal-container');
            return;
        }

        // Target the wrapper INSIDE the shadow root, not the host element
        const target = hostEl.shadowRoot?.getElementById('htmx-content-wrapper') || hostEl;

        const ids = this._getIds();
        const idSourceType = this._getIdSourceType();

        const ajaxConfig = {
            source: target,
            target: target,
            swap: 'innerHTML'
        };

        if (method === 'post' && ids.length > 0) {
            ajaxConfig.values = { selectedIds: ids.join(','), idSourceType: idSourceType };
        }

        htmx.ajax(method.toUpperCase(), url, ajaxConfig);
    }
}

// Guard against re-definition (see index.js / html-preview.js): the login document.write()
// re-executes this bundle in the same window, where 'next-link' is already defined.
if (!customElements.get('next-link')) {
    customElements.define('next-link', NextLink);
}